import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from '@env/environment';
import { STORAGE_NAME } from '@app/core/constants/constants';
import { generateCodeChallenge, generateCodeVerifier } from '@app/core/utils/utils';
import { AuthService } from '@app/core/services/auth.service';
import {CookieService} from "@app/core/services/cookie.service";

@Component({
  selector: 'app-sso-login',
  templateUrl: './sso-login.component.html',
  styleUrls: ['./sso-login.component.scss']
})
export class SsoLoginComponent implements OnInit {
  bgLoginPath = environment.bgLogin;
  logoPath = environment.logo;
  title = environment.title;
  copyright = environment.copyright;
  currentYear = new Date().getFullYear();
  isSubmitted = false;
  isFocus = false;
  state: string | null = null;
  form!: FormGroup;
  requestParams!: URLSearchParams;

  constructor(private fb: FormBuilder,
              private http: HttpClient,
              private toastService: ToastrService,
              private translate: TranslateService,
              private route: ActivatedRoute,
              private router: Router,
              private cookieService: CookieService,
              private authService: AuthService) {}

  ngOnInit(): void {
    this.initForm();
    let redirectUri = '';
    let loginError = '';
    this.state = this.route.snapshot.queryParamMap.get('state');
    this.route.queryParams.subscribe(async params => {
      loginError = params['login_error'] || '';
      redirectUri = params['redirect_uri'];
      if (loginError) {
        this.toastService.warning(loginError);
        return;
      }
      if (redirectUri) {
        this.requestParams = new URLSearchParams({
          response_type: params['response_type'] ?? '',
          client_id: params['client_id'] ?? '',
          redirect_uri: params['redirect_uri'] ?? '',
          scope: params['scope'] ?? '',
          code_challenge: params['code_challenge'] ?? '',
          code_challenge_method: params['code_challenge_method'] ?? ''
        });
      } else { // sso
        const codeVerifier = generateCodeVerifier();
        const codeChallenge = await generateCodeChallenge(codeVerifier);
        this.cookieService.set(STORAGE_NAME.PKCE_CODE_VERIFIER, codeVerifier);
        this.cookieService.set("codeChange", codeChallenge);
        this.requestParams = new URLSearchParams({
          response_type: 'code',
          client_id: environment.clientId,
          redirect_uri: `${environment.frontend}/callback`,
          scope: 'openid profile email offline_access',
          code_challenge: codeChallenge,
          code_challenge_method: 'S256',
        });
      }
    });

    if (!redirectUri && !loginError) {
      if (this.authService.isAuthenticated()) {
        this.router.navigateByUrl('/').then();
      } else {
        this.authService.checkIsLogin().then();
      }
    }
  }

  initForm() {
    this.form = this.fb.group({
      loginName: ['', Validators.required],
      password: ['', Validators.required]
    });
  }

  onSubmit() {
    this.isSubmitted = true;
    if (this.form.invalid) return;
    this.http.post<any>(`${environment.backend.sso}/v1/user/login?state=` + this.state, this.form.value, {withCredentials: true }).subscribe(res => {
      if (res.code === 'SUCCESS') {
        window.location.href = `${environment.backend.sso}/oauth2/authorize?state=` + this.state + `&${this.requestParams.toString()}`;
      } else if (res.code === 'CONTACT_CONFIRM_REQUIRED') {
        // Xóa token OAuth cũ — màn confirm-contact chỉ dùng HTTP session cookie
        this.cookieService.delete(STORAGE_NAME.ACCESS_TOKEN);
        this.cookieService.delete(STORAGE_NAME.REFRESH_TOKEN);
        this.cookieService.delete(STORAGE_NAME.EXPIRES_AT);
        this.cookieService.delete(STORAGE_NAME.ID_TOKEN);
        sessionStorage.setItem('ssoState', this.state || '');
        sessionStorage.setItem('ssoReqParams', this.requestParams.toString());
        if (res.data) {
          sessionStorage.setItem('ssoContactData', JSON.stringify(res.data));
        }
        this.router.navigate(['/confirm-contact']).then();
      } else if (res.code === 'MFA_REQUIRED' || res.code === 'MFA_SETUP_REQUIRED') {
        // Lưu ngữ cảnh OAuth để màn 2FA redirect tiếp sau khi xác thực thành công
        this.cookieService.delete(STORAGE_NAME.ACCESS_TOKEN);
        this.cookieService.delete(STORAGE_NAME.REFRESH_TOKEN);
        this.cookieService.delete(STORAGE_NAME.EXPIRES_AT);
        this.cookieService.delete(STORAGE_NAME.ID_TOKEN);
        sessionStorage.setItem('ssoState', this.state || '');
        sessionStorage.setItem('ssoReqParams', this.requestParams.toString());
        this.router.navigate([res.code === 'MFA_REQUIRED' ? '/verify-2fa' : '/setup-2fa']).then();
      }
    }, () => {
      this.toastService.error(this.translate.instant('common.validate.loginError'));
    });
  }

  get f() {
    return this.form.controls;
  }

  changePassword() {
    this.router.navigate(['/change-password']);
  }

  forgotPassword() {
    this.router.navigate(['/forgot-password']);
  }

  onLabelClick() {
    this.isFocus = !this.isFocus;
  }

  loginWithFacebook() {
    window.location.href = `${environment.backend.sso}/oauth2/authorize?idp=facebook&${this.requestParams.toString()}`;
  }

  loginWithOffice365() {
    window.location.href = `${environment.backend.sso}/oauth2/authorize?idp=azure&${this.requestParams.toString()}`;
  }

}

